import React, { useState, useMemo, useEffect } from "react";
import useAllUsers from "../../hooks/useAllUsers";
import useZohoUsers from "../../hooks/useZohoUsers";
import useRoles from "../../hooks/useRoles";
import Loader from "../../components/common/GlobalLoader";

const AllUsers = () => {
  const [userType, setUserType] = useState("Portal"); // "Portal" or "ZohoUsers"
  
  // Portal users hook
  const portalUsers = useAllUsers();
  
  // Zoho users hook
  const zohoUsers = useZohoUsers();
  
  // Select the appropriate hook based on userType
  const { users, loading, saving, error, count, fetchUsers, updateUserRole } = 
    userType === "Portal" ? portalUsers : { 
      users: zohoUsers.users, 
      loading: zohoUsers.loading, 
      saving: false, 
      error: zohoUsers.error, 
      count: zohoUsers.pagination?.totalCount || 0, 
      fetchUsers: () => zohoUsers.fetchUsers(zohoUsers.pagination.page, zohoUsers.pagination.limit), 
      updateUserRole: null 
    };
  
  const { roles, loading: rolesLoading } = useRoles();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [confirmationDialog, setConfirmationDialog] = useState(null);
  /** Native select menus mis-position on some mobile browsers; use custom list instead. */
  const [mobileRolePickerUserId, setMobileRolePickerUserId] = useState(null);

  // Determine the current logged-in user's role (from localStorage)
  let currentUserRoleName = "";
  let isCustomerAdminUser = false;

  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      const role = parsedUser?.role;

      if (typeof role === "string") {
        currentUserRoleName = role;
      } else if (role && typeof role === "object") {
        currentUserRoleName = role.name || role.role || "";
      }

      const upperRole = (currentUserRoleName || "").toUpperCase().replace(/\s+/g, "_");
      isCustomerAdminUser =
        upperRole === "CUSTOMER_ADMIN" || upperRole === "CUSTOMERADMIN";
    }
  } catch (err) {
    // Fail silently if parsing localStorage fails
    // eslint-disable-next-line no-console
    console.error("Failed to read current user role from localStorage:", err);
  }

  // Filter roles that can be assigned by the current user
  const assignableRoles = useMemo(() => {
    if (!roles || roles.length === 0) return [];

    return roles.filter((role) => {
      const name = (role.name || "").toUpperCase().replace(/\s+/g, "_");

      const isSuperAdminRole =
        name === "SUPER_ADMIN" || name === "SUPERADMIN";
      const isCustomerAdminRole =
        name === "CUSTOMER_ADMIN" || name === "CUSTOMERADMIN";

      // Super Admin role must NEVER be shown in the frontend
      if (isSuperAdminRole) return false;

      // When logged in as Customer Admin, hide the Customer Admin role as well
      if (isCustomerAdminUser && isCustomerAdminRole) return false;

      return true;
    });
  }, [roles, isCustomerAdminUser]);

  // Helper function to get user initials
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Filter users based on search query (client-side for both types)
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return users;
    }
    const query = searchQuery.toLowerCase().trim();
    
    if (userType === "ZohoUsers") {
      // Zoho users: filter by Contact Name, Display Name, EmailID, or Company Name
      return users.filter(
        (user) =>
          (user["Contact Name"] && user["Contact Name"].toLowerCase().includes(query)) ||
          (user["Display Name"] && user["Display Name"].toLowerCase().includes(query)) ||
          (user.EmailID && user.EmailID.toLowerCase().includes(query)) ||
          (user["Company Name"] && user["Company Name"].toLowerCase().includes(query))
      );
    }
    
    // Portal users: client-side filtering
    return users.filter(
      (user) =>
        (user.CustomerName &&
          user.CustomerName.toLowerCase().includes(query)) ||
        (user.email && user.email.toLowerCase().includes(query))
    );
  }, [users, searchQuery, userType]);

  // Reset to page 1 when search query or user type changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, userType]);

  // Fetch Zoho users when page, itemsPerPage, or userType changes
  useEffect(() => {
    if (userType === "ZohoUsers") {
      zohoUsers.fetchUsers(currentPage, itemsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, userType]);

  // Calculate pagination
  // For Zoho users, we use server-side pagination if no search query, otherwise client-side
  const useServerPagination = userType === "ZohoUsers" && !searchQuery.trim();
  
  const totalPages = useServerPagination
    ? (zohoUsers.pagination?.totalPages || 1)
    : Math.ceil(filteredUsers.length / itemsPerPage);
  
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  const paginatedUsers = useServerPagination
    ? users // Already paginated by server
    : filteredUsers.slice(startIndex, endIndex);
  
  // For display purposes
  const displayStartIndex = useServerPagination
    ? ((currentPage - 1) * itemsPerPage) + 1
    : startIndex + 1;
  
  const displayEndIndex = useServerPagination
    ? Math.min(currentPage * itemsPerPage, zohoUsers.pagination?.totalCount || 0)
    : Math.min(endIndex, filteredUsers.length);
  
  const displayTotal = useServerPagination
    ? (zohoUsers.pagination?.totalCount || 0)
    : filteredUsers.length;

  // Generate page numbers to display (always show exactly 3 pages)
  const getPageNumbers = () => {
    const pages = [];
    
    if (totalPages <= 3) {
      // Show all pages if total pages is 3 or less
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show exactly 3 consecutive pages
      if (currentPage === 1) {
        // At the start: show 1, 2, 3
        pages.push(1, 2, 3);
      } else if (currentPage === totalPages) {
        // At the end: show last 3 pages
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        // In the middle: show current-1, current, current+1
        pages.push(currentPage - 1, currentPage, currentPage + 1);
      }
    }
    return pages;
  };

  // Helper function to get current role ID for a user
  const getCurrentRoleId = (user) => {
    // Return empty if no role or roles not loaded
    if (!user.role || !roles || roles.length === 0) return "";
    
    // Handle different role formats from API
    let roleValue = user.role;
    
    // If role is an object, extract the ID or name
    if (typeof user.role === 'object' && user.role !== null) {
      roleValue = user.role._id || user.role.name || "";
    }
    
    if (!roleValue) return "";
    
    // Try to find role by ID first (exact match)
    let role = roles.find((r) => r._id === roleValue);
    
    // If not found by ID, try by name (exact match)
    if (!role) {
      role = roles.find((r) => r.name === roleValue);
    }
    
    // Return the role ID if found, otherwise empty string
    return role ? role._id : "";
  };

  const handleRoleDropdownChange = (user, newRoleId) => {
    setMobileRolePickerUserId(null);
    const currentRoleId = getCurrentRoleId(user);
    
    // If no change, do nothing
    if (newRoleId === currentRoleId) {
      return;
    }

    // Find role names for display
    const currentRole = roles.find((r) => r._id === currentRoleId);
    const newRole = roles.find((r) => r._id === newRoleId);
    
    // Show confirmation dialog
    setConfirmationDialog({
      userId: user._id,
      userEmail: user.email,
      userName: user.CustomerName || user.email,
      currentRoleName: currentRole?.name || "No role",
      newRoleId: newRoleId,
      newRoleName: newRole?.name || "Selected role",
    });
  };

  const handleConfirmRoleChange = async () => {
    if (!confirmationDialog) return;
    
    const { userId, userEmail, newRoleId } = confirmationDialog;
    await updateUserRole(userId, userEmail, newRoleId);
    setConfirmationDialog(null);
    // Refresh users list after update
    fetchUsers();
  };

  const handleCancelRoleChange = () => {
    setConfirmationDialog(null);
  };

  useEffect(() => {
    if (mobileRolePickerUserId == null) return;
    const onDocPointerDown = (e) => {
      const root = document.querySelector(
        `[data-mobile-role-picker="${String(mobileRolePickerUserId)}"]`
      );
      if (root && !root.contains(e.target)) {
        setMobileRolePickerUserId(null);
      }
    };
    document.addEventListener("mousedown", onDocPointerDown);
    document.addEventListener("touchstart", onDocPointerDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDocPointerDown);
      document.removeEventListener("touchstart", onDocPointerDown);
    };
  }, [mobileRolePickerUserId]);

  return (
    <div className="relative">
      {(loading || saving || rolesLoading) && (
        <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-lg z-40">
          <Loader />
        </div>
      )}

      <div
        className={`bg-white border rounded-xl p-3 sm:p-4 md:p-6 shadow-md max-w-7xl mx-auto ${
          loading || saving || rolesLoading ? "opacity-40 pointer-events-none select-none" : ""
        }`}
      >
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold theme-text-primary">
              All Users
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              View all users in the system. Total:{" "}
              <span className="font-semibold text-gray-900">{count}</span> users
              {filteredUsers.length !== count && (
                <span className="ml-2">
                  ({filteredUsers.length} match your search)
                </span>
              )}
            </p>
          </div>
          <button
            onClick={() => {
              if (userType === "Portal") {
                fetchUsers();
              } else {
                zohoUsers.fetchUsers(currentPage, itemsPerPage);
              }
            }}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-md theme-bg-primary px-4 py-2 text-sm font-semibold shadow-sm hover:shadow-md transition-all focus:outline-none focus:ring-2 theme-ring-primary focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              stroke="currentColor"
              className="h-4 w-4 mr-2"
              viewBox="0 0 24 24"
            >
              <path
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {error}
          </div>
        )}

        {/* Search Input, User Type Dropdown, and Items Per Page */}
        <div className="mb-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative flex-1 w-full sm:w-auto">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-gray-500 focus:border-gray-500 text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                type="button"
              >
                <svg
                  className="h-5 w-5 text-gray-400 hover:text-gray-600"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
            <label htmlFor="user-type-select" className="text-sm text-gray-700 whitespace-nowrap">
              User Type:
            </label>
            <select
              id="user-type-select"
              value={userType}
              onChange={(e) => {
                setUserType(e.target.value);
                setCurrentPage(1);
                setSearchQuery("");
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 bg-white min-w-[140px] flex-1 sm:flex-none"
            >
              <option value="Portal">Portal</option>
              <option value="ZohoUsers">ZohoUsers</option>
            </select>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
            <label htmlFor="items-per-page-select" className="text-sm text-gray-700 whitespace-nowrap">
              Items per page:
            </label>
            <select
              id="items-per-page-select"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 bg-white flex-1 sm:flex-none"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {users.length === 0 && !loading ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1">
              No {userType === "ZohoUsers" ? "Zoho " : ""}users found
            </p>
            <p className="text-xs text-gray-500">Try refreshing the page</p>
          </div>
        ) : (userType === "Portal" && filteredUsers.length === 0) ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1">No users match your search</p>
            <p className="text-xs text-gray-500">
              Try adjusting your search criteria
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="mt-3 text-xs text-gray-600 hover:text-gray-900 underline"
                type="button"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <>
          {/* Mobile: stacked cards (no horizontal table scroll) */}
          <div className="md:hidden space-y-3">
            {paginatedUsers.map((user, index) => {
              const rowKey = user._id || user["Contact ID"] || `m-${index}`;
              const customerName = userType === "ZohoUsers"
                ? (user["Contact Name"] || user["Display Name"] || "")
                : (user.CustomerName || "");
              const email = userType === "ZohoUsers"
                ? (user.EmailID || "")
                : (user.email || "");
              const companyName = userType === "ZohoUsers"
                ? (user["Company Name"] || "")
                : (user.CompanyName || "");
              const phone = user.phone || "";
              const status = userType === "ZohoUsers" ? (user.Status || "") : null;
              const currentRoleIdMobile = userType === "Portal" ? getCurrentRoleId(user) : "";
              const currentRoleNameMobile =
                userType === "Portal"
                  ? (roles.find((r) => r._id === currentRoleIdMobile)?.name || "Select role...")
                  : "";

              return (
                <div
                  key={rowKey}
                  className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm ${
                    mobileRolePickerUserId === rowKey ? "relative z-40" : ""
                  }`}
                >
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Name
                      </p>
                      <p className="mt-0.5 font-semibold text-gray-900 break-words">
                        {customerName || (
                          <span className="text-gray-400 italic font-normal">Not provided</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                        Email
                      </p>
                      <div className="mt-0.5 break-all">
                        {email ? (
                          <a
                            href={`mailto:${email}`}
                            className="text-gray-900 hover:text-gray-700 hover:underline"
                          >
                            {email}
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">Not provided</span>
                        )}
                      </div>
                    </div>
                    {companyName ? (
                      <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                          Company
                        </p>
                        <p className="mt-0.5">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 break-words">
                            {companyName}
                          </span>
                        </p>
                      </div>
                    ) : null}
                    {userType === "Portal" && (
                      <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                          Phone
                        </p>
                        <div className="mt-0.5">
                          {phone ? (
                            <a
                              href={`tel:${phone}`}
                              className="text-gray-900 hover:underline inline-flex items-center gap-1"
                            >
                              {phone}
                            </a>
                          ) : (
                            <span className="text-gray-400 italic">Not provided</span>
                          )}
                        </div>
                      </div>
                    )}
                    {userType === "ZohoUsers" && (
                      <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                          Status
                        </p>
                        <div className="mt-0.5">
                          {status ? (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                status === "Active"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {status}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">Not provided</span>
                          )}
                        </div>
                      </div>
                    )}
                    {userType === "Portal" && (
                      <div className="pt-1">
                        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                          Role
                        </span>
                        <div
                          data-mobile-role-picker={rowKey}
                          className="relative"
                        >
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() =>
                              setMobileRolePickerUserId((prev) =>
                                prev === rowKey ? null : rowKey
                              )
                            }
                            className="w-full min-h-[44px] flex items-center justify-between gap-2 border border-gray-300 rounded-md px-3 py-2.5 text-sm text-left bg-white focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-0 disabled:opacity-50"
                            aria-expanded={mobileRolePickerUserId === rowKey}
                            aria-haspopup="listbox"
                          >
                            <span className="truncate">{currentRoleNameMobile}</span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
                                mobileRolePickerUserId === rowKey ? "rotate-180" : ""
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                          {mobileRolePickerUserId === rowKey && (
                            <ul
                              className="absolute left-0 right-0 top-full mt-1 max-h-52 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                              role="listbox"
                            >
                              {assignableRoles.length === 0 ? (
                                <li className="px-3 py-2 text-sm text-gray-500">
                                  No roles available
                                </li>
                              ) : (
                                assignableRoles.map((role) => (
                                  <li key={role._id}>
                                    <button
                                      type="button"
                                      role="option"
                                      className={`w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 ${
                                        currentRoleIdMobile === role._id
                                          ? "bg-indigo-50 font-medium text-gray-900"
                                          : "text-gray-800"
                                      }`}
                                      onClick={() =>
                                        handleRoleDropdownChange(user, role._id)
                                      }
                                    >
                                      {role.name}
                                    </button>
                                  </li>
                                ))
                              )}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* md+: data table */}
          <div className="hidden md:block overflow-hidden border border-gray-200 rounded-lg shadow-sm">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          <div className="flex items-center gap-1 sm:gap-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            <span className="hidden sm:inline">Customer Name</span>
                            <span className="sm:hidden">Name</span>
                          </div>
                        </th>
                        <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          <div className="flex items-center gap-1 sm:gap-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                            <span>Email</span>
                          </div>
                        </th>
                        <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">
                          <div className="flex items-center gap-1 sm:gap-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                            <span className="hidden lg:inline">Company Name</span>
                            <span className="lg:hidden">Company</span>
                          </div>
                        </th>
                        {userType === "Portal" && (
                          <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden lg:table-cell">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                />
                              </svg>
                              <span>Phone</span>
                            </div>
                          </th>
                        )}
                        {userType === "ZohoUsers" && (
                          <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              <span>Status</span>
                            </div>
                          </th>
                        )}
                        {userType === "Portal" && (
                          <th className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            <div className="flex items-center gap-1 sm:gap-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                />
                              </svg>
                              <span>Role</span>
                            </div>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedUsers.map((user, index) => {
                        // Normalize user data based on type
                        const customerName = userType === "ZohoUsers" 
                          ? (user["Contact Name"] || user["Display Name"] || "")
                          : (user.CustomerName || "");
                        const email = userType === "ZohoUsers"
                          ? (user.EmailID || "")
                          : (user.email || "");
                        const companyName = userType === "ZohoUsers"
                          ? (user["Company Name"] || "")
                          : (user.CompanyName || "");
                        const phone = user.phone || "";
                        const status = userType === "ZohoUsers" ? (user.Status || "") : null;
                        
                        return (
                          <tr
                            key={user._id || user["Contact ID"] || index}
                            className="hover:bg-gray-50 transition-colors duration-150"
                          >
                            <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                              <div className="text-xs sm:text-sm font-semibold text-gray-900">
                                {customerName || (
                                  <span className="text-gray-400 italic">Not provided</span>
                                )}
                              </div>
                              {/* Show company on mobile in the name cell */}
                              {companyName && (
                                <div className="mt-1 md:hidden">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    {companyName}
                                  </span>
                                </div>
                              )}
                              {/* Show phone on mobile in the name cell (Portal only) */}
                              {userType === "Portal" && phone && (
                                <div className="mt-1 lg:hidden">
                                  <a
                                    href={`tel:${phone}`}
                                    className="text-xs text-gray-600 hover:text-gray-900 hover:underline flex items-center gap-1"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-3 w-3"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                      />
                                    </svg>
                                    {phone}
                                  </a>
                                </div>
                              )}
                            </td>
                            <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                              <div className="text-xs sm:text-sm text-gray-700">
                                {email ? (
                                  <a
                                    href={`mailto:${email}`}
                                    className="text-gray-900 hover:text-gray-700 hover:underline break-words"
                                  >
                                    {email}
                                  </a>
                                ) : (
                                  <span className="text-gray-400 italic">Not provided</span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 hidden md:table-cell">
                              <div className="text-xs sm:text-sm text-gray-700">
                                {companyName ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    {companyName}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 italic">Not provided</span>
                                )}
                              </div>
                            </td>
                            {userType === "Portal" && (
                              <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 hidden lg:table-cell">
                                <div className="text-xs sm:text-sm text-gray-700">
                                  {phone ? (
                                    <a
                                      href={`tel:${phone}`}
                                      className="text-gray-900 hover:text-gray-700 hover:underline whitespace-nowrap"
                                    >
                                      {phone}
                                    </a>
                                  ) : (
                                    <span className="text-gray-400 italic">Not provided</span>
                                  )}
                                </div>
                              </td>
                            )}
                            {userType === "ZohoUsers" && (
                              <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                                <div className="text-xs sm:text-sm text-gray-700">
                                  {status ? (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      status === "Active" 
                                        ? "bg-green-100 text-green-800" 
                                        : "bg-gray-100 text-gray-800"
                                    }`}>
                                      {status}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 italic">Not provided</span>
                                  )}
                                </div>
                              </td>
                            )}
                            {userType === "Portal" && (
                              <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                                <select
                                  value={getCurrentRoleId(user)}
                                  onChange={(e) => handleRoleDropdownChange(user, e.target.value)}
                                  className="w-full min-w-[120px] sm:min-w-[150px] border border-gray-300 rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 bg-white"
                                  disabled={saving}
                                >
                                  <option value="">Select role...</option>
                                  {assignableRoles.map((role) => (
                                    <option key={role._id} value={role._id}>
                                      {role.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          </>
        )}

        {/* Pagination */}
        {((userType === "Portal" && filteredUsers.length > 0) || (userType === "ZohoUsers" && users.length > 0)) && totalPages > 1 && (
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-semibold text-gray-900">
                {displayStartIndex}
              </span>{" "}
              to{" "}
              <span className="font-semibold text-gray-900">
                {displayEndIndex}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-900">
                {displayTotal}
              </span>{" "}
              results
            </div>

            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={useServerPagination
                  ? !zohoUsers.pagination?.hasPrevPage 
                  : currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {getPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
                      currentPage === page
                        ? "bg-gray-600 text-white"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              {/* Next Button */}
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={useServerPagination
                  ? !zohoUsers.pagination?.hasNextPage
                  : currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmationDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-blue-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  Update User Role
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Do you want to update the role?
                </p>
              </div>
            </div>

            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    User
                  </p>
                  <p className="text-sm text-gray-900 mt-1">
                    {confirmationDialog.userName}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Current Role
                    </p>
                    <p className="text-sm text-gray-900 mt-1 break-words">
                      {confirmationDialog.currentRoleName}
                    </p>
                  </div>
                  <div className="flex-shrink-0 hidden sm:flex justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0 sm:text-right">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      New Role
                    </p>
                    <p className="text-sm font-semibold text-blue-600 mt-1 break-words">
                      {confirmationDialog.newRoleName}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                onClick={handleCancelRoleChange}
                disabled={saving}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRoleChange}
                disabled={saving}
                className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
              >
                {saving ? "Updating..." : "Yes, Update Role"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllUsers;
